import { IOrganizationEntity } from '@novu/shared';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_HOSTNAME } from '@/config';
import { AuthContextProvider } from './auth.resource';
import { ClerkLoaded } from './clerk-loaded';
import {
  OrganizationList,
  OrganizationProfile,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  UserProfile,
} from './components';
import { clearJwtToken, getJwtToken, isJwtValid, setJwtToken } from './jwt-manager';
import { OrganizationContextProvider, useOrganization } from './organization.resource';
import { OrganizationSwitcher } from './organization-switcher';
import { Show } from './show';
import { useAuth } from './use-auth';
import { UserContextProvider, useUser } from './user.resource';
import { UserButton } from './user-button';

type SelfHostedApiResponse<T> = {
  data: T;
};

type SelfHostedMembership = {
  id: string;
  organization: {
    id: string;
    name: string;
    slug?: string | null;
    imageUrl: string;
    publicMetadata: Record<string, unknown>;
  };
};

type SelfHostedListOrganization = IOrganizationEntity & {
  id?: string;
  slug?: string;
};

type UseOrganizationListOptions = {
  userMemberships?: {
    infinite?: boolean;
    pageSize?: number;
  };
};

function parseApiError(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') {
    return fallback;
  }

  const message = (payload as { message?: unknown }).message;

  if (typeof message === 'string' && message.trim().length > 0) {
    return message;
  }

  if (Array.isArray(message)) {
    const first = message.find((entry) => typeof entry === 'string' && entry.trim().length > 0);
    if (typeof first === 'string') {
      return first;
    }
  }

  return fallback;
}

function syncSelfHostedClerkGlobal() {
  if (typeof window === 'undefined') {
    return;
  }

  (window as any).Clerk = {
    ...((window as any).Clerk || {}),
    loggedIn: isJwtValid(getJwtToken()),
    session: {
      getToken: async () => getJwtToken(),
    },
  };
}

async function selfHostedRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getJwtToken();

  if (!token) {
    throw new Error('Missing authentication token');
  }

  const response = await fetch(`${API_HOSTNAME}/v1${path}`, {
    method: init?.method || 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
    body: init?.body,
  });

  let payload: SelfHostedApiResponse<T> | null = null;

  try {
    payload = (await response.json()) as SelfHostedApiResponse<T>;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const message = parseApiError(payload, `Request failed (${response.status})`);
    const isAuthFailure =
      response.status === 401 ||
      response.status === 403 ||
      message.toLowerCase().includes('unauthorized') ||
      message.toLowerCase().includes('user not found');

    if (isAuthFailure) {
      clearJwtToken();
      syncSelfHostedClerkGlobal();
    }

    throw new Error(message);
  }

  if (!payload) {
    throw new Error('Invalid API response');
  }

  return payload.data;
}

export {
  AuthContextProvider,
  ClerkLoaded,
  OrganizationContextProvider,
  OrganizationList,
  OrganizationProfile,
  OrganizationSwitcher,
  RedirectToSignIn,
  Show,
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  UserButton,
  UserProfile,
};

export { useAuth, useOrganization, useUser };

export const useClerk = () => {
  const { isLoaded } = useAuth();

  useEffect(() => {
    syncSelfHostedClerkGlobal();
  }, []);

  const setActive = useCallback(async ({ organization }: { organization?: string | null } = {}) => {
    if (!organization) {
      return;
    }

    const switchedToken = await selfHostedRequest<string>(`/auth/organizations/${organization}/switch`, {
      method: 'POST',
    });

    setJwtToken(switchedToken);
    syncSelfHostedClerkGlobal();
  }, []);

  const signOut = useCallback(async ({ redirectUrl }: { redirectUrl?: string } = {}) => {
    clearJwtToken();
    syncSelfHostedClerkGlobal();

    if (redirectUrl) {
      window.location.assign(redirectUrl);
    }
  }, []);

  return {
    loaded: isLoaded,
    setActive,
    signOut,
    session: {
      getToken: async () => getJwtToken(),
    },
  };
};

export const useOrganizationList = (_options?: UseOrganizationListOptions) => {
  const { organization, isLoaded: isOrganizationLoaded } = useOrganization() as {
    organization: IOrganizationEntity | undefined;
    isLoaded: boolean;
  };
  const [organizations, setOrganizations] = useState<SelfHostedListOrganization[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const revalidate = useCallback(async () => {
    const hasToken = Boolean(getJwtToken());

    if (!hasToken) {
      setOrganizations([]);
      setHasLoadedOnce(true);
      setIsFetching(false);

      return;
    }

    setIsFetching(true);

    try {
      const data = await selfHostedRequest<SelfHostedListOrganization[]>('/organizations');
      setOrganizations(data || []);
    } finally {
      setIsFetching(false);
      setHasLoadedOnce(true);
    }
  }, []);

  useEffect(() => {
    if (!isOrganizationLoaded) {
      return;
    }

    void revalidate();
  }, [isOrganizationLoaded, revalidate]);

  const memberships = useMemo<SelfHostedMembership[]>(
    () =>
      organizations.map((org) => {
        const organizationId = org._id || org.id || '';

        return {
          id: organizationId,
          organization: {
            id: organizationId,
            name: org.name,
            slug: org.slug || null,
            imageUrl: '',
            publicMetadata: {
              externalOrgId: organizationId,
            },
          },
        };
      }),
    [organizations]
  );

  const setActive = useCallback(
    async ({ organization: organizationId }: { organization?: string | null }) => {
      if (!organizationId) {
        return;
      }

      const switchedToken = await selfHostedRequest<string>(`/auth/organizations/${organizationId}/switch`, {
        method: 'POST',
      });

      setJwtToken(switchedToken);
      syncSelfHostedClerkGlobal();
      await revalidate();
    },
    [revalidate]
  );

  const createOrganization = useCallback(
    async ({ name }: { name: string; slug?: string }) => {
      const created = await selfHostedRequest<SelfHostedListOrganization>('/organizations', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });

      const id = created._id || created.id;

      if (!id) {
        throw new Error('Created organization is missing an id');
      }

      await revalidate();

      return {
        id,
        name: created.name,
      };
    },
    [revalidate]
  );

  return {
    isLoaded: isOrganizationLoaded && hasLoadedOnce,
    organizationList: organization ? [organization] : organizations,
    userMemberships: {
      data: memberships,
      revalidate,
      isFetching,
      hasNextPage: false,
      fetchNext: undefined,
    },
    setActive,
    createOrganization,
  };
};

export const ClerkContext = React.createContext({});

export type ProtectProps = {
  children: React.ReactNode;
  [key: string]: any;
};

export const Protect = ({ children, ...rest }: ProtectProps) => {
  return children;
};

export function ClerkProvider({ children }: any) {
  const value = {};

  useEffect(() => {
    syncSelfHostedClerkGlobal();
  }, []);

  return (
    <ClerkContext.Provider value={value}>
      <UserContextProvider>
        <AuthContextProvider>
          <OrganizationContextProvider>{children}</OrganizationContextProvider>
        </AuthContextProvider>
      </UserContextProvider>
    </ClerkContext.Provider>
  );
}

syncSelfHostedClerkGlobal();

export type DecodedJwt = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  organizationId: string;
  environmentId: string | null;
  roles: string[];
  iat: number;
  exp: number;
  iss: string;
};
